import { CheckIcon, CircleAlertIcon, MinusIcon } from 'lucide-react';
import { WorkspaceIcon } from '@/workspace/icons';
import { useRef } from 'react';
import { settingsChangeFromTool } from '@/lib/account-settings';
import { useSettingsUndo } from '@/workspace/use-settings-undo';
import { useMemoryUndo } from '@/workspace/use-memory-undo';
import { useAccountSettings } from '@/lib/account-settings';
import { compactActionLabel } from './action-labels';
import { statusLabel, useActionSummary } from './action-approval-copy';
import type { ActionView } from './action-confirmation';
import type { useActionConfirmation } from './use-action-confirmation';

type State = ReturnType<typeof useActionConfirmation>;

/** Domain-owned Undo is optional. Loading a receipt never executes or applies it.
 * `fresh` marks a receipt that replaced its row in this session, so only that one
 * crossfades in; receipts in an older, reopened thread render still. */
export function ActionReceipt({
  action,
  state,
  fresh = false,
}: {
  action: ActionView;
  state: State;
  fresh?: boolean;
}) {
  const { workspace, actionText, surfaceCopy, locale, origin } = state;
  const change =
    action.status === 'executed' ? settingsChangeFromTool(action.tool, action.result) : null;
  const settingsUndo = useSettingsUndo(change, action.id);
  const memoryUndo = useMemoryUndo(
    action.account,
    action.tool,
    action.status === 'executed' ? action.result : null,
  );
  const undo = action.tool === 'save_memory' ? memoryUndo : settingsUndo;
  const account = useAccountSettings();
  // A cleared setting has no stored choice; do not show a raw null or invent a restored value.
  const restoredChoiceKnown =
    !change || Object.values(change.previous).every((value) => value != null);
  const root = useRef<HTMLElement>(null);
  const summarize = useActionSummary();
  const summary = compactActionLabel(
    action,
    surfaceCopy,
    actionText,
    locale,
    action.status === 'executed',
    summarize(action),
  );
  const own = action.account === account?.userId && !workspace?.readOnly;
  const manageMemory =
    own &&
    action.status === 'executed' &&
    ['save_memory', 'forget_memory'].includes(action.tool) &&
    !undo.undoable &&
    undo.state !== 'undone';
  const details = () => {
    if (!workspace) return;
    if (origin)
      workspace.openCanvas({
        kind: 'activity',
        id: origin.messageId,
        tab: 'steps',
        step: origin.toolCallId,
      });
    else workspace.openCanvas({ kind: 'action', id: action.id, tab: 'details' });
  };
  return (
    <section
      ref={root}
      className="pc-action-receipt"
      data-status={action.status}
      data-fresh={fresh ? 'true' : undefined}
      tabIndex={-1}
    >
      <ReceiptGlyph status={action.status} />
      <span role="status" className="pc-action-receipt-text">
        {undo.state === 'undone'
          ? !restoredChoiceKnown
            ? surfaceCopy.undone
            : surfaceCopy.afterUndo(
                change
                  ? compactActionLabel(
                      { ...action, args: change.previous },
                      surfaceCopy,
                      actionText,
                      locale,
                      true,
                    )
                  : summary,
              )
          : summary}
        {action.status !== 'executed' && ` · ${statusLabel(action.status, actionText)}`}
      </span>
      {undo.undoable && (
        <button
          type="button"
          disabled={undo.state === 'undoing'}
          onClick={async () => {
            await undo.undo();
            root.current?.focus({ preventScroll: true });
          }}
        >
          {surfaceCopy.undo}
        </button>
      )}
      {manageMemory && (
        <button type="button" onClick={() => workspace?.navigate('personal-memory')}>
          {surfaceCopy.manage}
        </button>
      )}
      {workspace && (
        <button
          type="button"
          className="pc-action-receipt-details"
          data-canvas-trigger
          aria-label={surfaceCopy.details}
          onClick={details}
        >
          <WorkspaceIcon name="chevron" size={14} />
        </button>
      )}
      {undo.state === 'failed' && <span role="alert">{surfaceCopy.undoFailed}</span>}
      {action.error && (
        <span className="pc-action-confirmation-error" role="alert">
          {action.error}
        </span>
      )}
    </section>
  );
}

/** Presentation only: the outcome as one 14px glyph ahead of the receipt line. */
function ReceiptGlyph({ status }: { status: ActionView['status'] }) {
  if (status === 'executed')
    return <CheckIcon className="pc-action-receipt-glyph" aria-hidden="true" />;
  if (status === 'failed')
    return <CircleAlertIcon className="pc-action-receipt-glyph" aria-hidden="true" />;
  if (status === 'rejected' || status === 'expired' || status === 'superseded')
    return <MinusIcon className="pc-action-receipt-glyph" aria-hidden="true" />;
  return null;
}
